import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useRef, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { Icon } from '../../Icon/Icon.es.js';
import { SidenavCollapsibleGroupItem } from '../Sidenav.CollapsibleGroup.Item/Sidenav.CollapsibleGroup.Item.es.js';
import { SidenavCollapsibleGroupHeader } from './Sidenav.CollapsibleGroup.Header/Sidenav.CollapsibleGroup.Header.es.js';

const SidenavCollapsibleGroup = ({ defaultCollapsedState = true, groupName, className, name, href, active = false, onNavigate, children, ...props })=>{
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsedState);
    const prevActiveRef = useRef(active);
    // Single useMemo that computes final value AND updates state when needed
    const isCollapsed = useMemo(()=>{
        const activeChanged = active !== prevActiveRef.current;
        const wasInactive = prevActiveRef.current === false;
        const isNowActive = active === true;
        // Update the prev ref for next comparison
        prevActiveRef.current = active;
        // If group just became active and is currently collapsed, expand it
        if (activeChanged && wasInactive && isNowActive && href && internalCollapsed) {
            setInternalCollapsed(false);
            return false; // Return the new expanded state
        }
        // Otherwise, return the current internal state
        return internalCollapsed;
    }, [
        active,
        href,
        internalCollapsed
    ]);
    const toggleCollapse = useCallback(()=>{
        setInternalCollapsed(!isCollapsed);
    }, [
        isCollapsed
    ]);
    // Handle navigation and state change in one onClick
    const handleClick = useCallback((event)=>{
        if (!href) {
            // Non-navigable group - just toggle
            toggleCollapse();
            return;
        }
        if (active) {
            // Active navigable group - just toggle, don't navigate
            event.preventDefault();
            toggleCollapse();
            return;
        }
        if (onNavigate) {
            // Inactive navigable group - handle navigation
            event.preventDefault();
            onNavigate(href);
        }
    }, [
        href,
        active,
        toggleCollapse,
        onNavigate
    ]);
    return jsxs("div", {
        ...props,
        children: [
            jsx(SidenavCollapsibleGroupHeader, {
                active: active,
                href: href,
                onClick: handleClick,
                children: jsxs("span", {
                    className: "inner",
                    children: [
                        name && jsx(Icon, {
                            className: "item-icon",
                            name: name,
                            size: 20
                        }),
                        jsx("span", {
                            className: "label",
                            children: groupName
                        }),
                        jsx(Icon, {
                            className: classNames('icon', {
                                reverse: isCollapsed
                            }),
                            name: "ChevronUpSolid",
                            size: 20
                        })
                    ]
                })
            }),
            jsx("span", {
                className: classNames('cb-group-items', className, {
                    collapsed: isCollapsed
                }),
                "data-testid": "sidenav-collapsible-group-items",
                children: children
            })
        ]
    });
};
SidenavCollapsibleGroup.displayName = 'Sidenav.CollapsibleGroup';
SidenavCollapsibleGroup.Item = SidenavCollapsibleGroupItem;

export { SidenavCollapsibleGroup };
//# sourceMappingURL=Sidenav.CollapsibleGroup.es.js.map
